import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, User, Building2, Mail, Phone, Home } from 'lucide-react';

export default function ContactInfoScreen({ onComplete, onBack }) {
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    societe: '',
    nombreLogements: '',
    email: '',
    telephone: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }
    
    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onComplete(formData);
    }
  };

  const isFormValid = formData.prenom.trim() && 
                      formData.nom.trim() && 
                      formData.email.trim() && 
                      formData.telephone.trim();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="w-full border-b border-border bg-background">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="w-4 h-4 text-primary" />
            <span>Tes coordonnées</span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full animate-fade-in">
          <Card className="border-border shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl sm:text-2xl">
                Avant de voir tes résultats
              </CardTitle>
              <CardDescription className="text-base">
                Ces informations nous permettent de personnaliser ton diagnostic et de te recontacter si tu le souhaites.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Prénom & Nom */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prenom" className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    Prénom *
                  </Label>
                  <Input
                    id="prenom"
                    placeholder="Ton prénom"
                    value={formData.prenom}
                    onChange={handleChange('prenom')}
                    className={errors.prenom ? 'border-destructive' : ''}
                  />
                  {errors.prenom && (
                    <p className="text-sm text-destructive">{errors.prenom}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom" className="flex items-center gap-2">
                    Nom *
                  </Label>
                  <Input
                    id="nom"
                    placeholder="Ton nom"
                    value={formData.nom}
                    onChange={handleChange('nom')}
                    className={errors.nom ? 'border-destructive' : ''}
                  />
                  {errors.nom && (
                    <p className="text-sm text-destructive">{errors.nom}</p>
                  )}
                </div>
              </div>

              {/* Société */}
              <div className="space-y-2">
                <Label htmlFor="societe" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  Société
                </Label>
                <Input
                  id="societe"
                  placeholder="Nom de ta société (optionnel)"
                  value={formData.societe}
                  onChange={handleChange('societe')}
                />
              </div>

              {/* Nombre de logements */}
              <div className="space-y-2">
                <Label htmlFor="nombreLogements" className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-muted-foreground" />
                  Nombre de logements gérés
                </Label>
                <Input
                  id="nombreLogements"
                  placeholder="Ex: 3"
                  value={formData.nombreLogements}
                  onChange={handleChange('nombreLogements')}
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  Adresse email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ton@email.com"
                  value={formData.email}
                  onChange={handleChange('email')}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              {/* Téléphone */}
              <div className="space-y-2">
                <Label htmlFor="telephone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  Téléphone *
                </Label>
                <Input
                  id="telephone"
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={formData.telephone}
                  onChange={handleChange('telephone')}
                  className={errors.telephone ? 'border-destructive' : ''}
                />
                {errors.telephone && (
                  <p className="text-sm text-destructive">{errors.telephone}</p>
                )}
              </div>

              <p className="text-xs text-muted-foreground pt-2">
                * Champs obligatoires. Tes données sont confidentielles et ne seront jamais partagées.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer with navigation */}
      <footer className="w-full border-t border-border bg-background sticky bottom-0">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={onBack}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
          
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className="gap-2"
          >
            Voir mes résultats
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </footer>
    </div>
  );
}
